import React from "react";

import { formatIarFeePercent, toTieredEntries } from "./iarFeeTieredUtils";
import {
  getFusionAllocationDisplayName,
  getProgramDisplayName,
  isPrivateClientStrategyAlias,
} from "../../utils/proposalUtils";
import {
  ELEMENTS_INITIAL_FEE_STAGE_HEADING,
  getElementsPathFeeStageHeading,
  isElementsStrategyName,
} from "./elementsFeeUtils";
import { normalizeFeeUpdateAccounts } from "./feeUpdateAccountUtils";
import { getVisibleIarFeeScheduleName } from "./iarFeeScheduleUtils";
import {
  getRegistrationDetailReviewRows,
  isStrategyChangeWorkflowAction,
} from "./ispBusinessRules";
import {
  resolveFusionSubFees,
  validateManagementFeeTable,
} from "./fusionFeeUtils";
import {
  isPcsRegistrationStrategy,
  supportsPcsRegistrationType,
} from "./pcsRegistrationUtils";
import {
  isStandardRegistrationStrategy,
  supportsStandardRegistrationType,
} from "./standardRegistrationUtils";

// Compact label/value row — replaces the prior tile grid. Grouped under
// section headings below so the confirm view is scannable on a single laptop
// screen for typical (single-client, non-Fusion) submissions.
const Row = ({ label, value, wrapValue = false }) => (
  <div
    className={`d-flex py-1 isp-confirm-row${
      wrapValue ? " isp-confirm-row-wrap" : ""
    }`}
    style={{
      borderBottom: "1px solid #f0f0f0",
      fontSize: "14px",
      flexWrap: wrapValue ? "wrap" : "nowrap",
    }}
  >
    <div
      className="isp-confirm-row-label"
      style={{
        flex: "0 0 240px",
        color: "#6c757d",
        paddingRight: "12px",
      }}
    >
      {label}
    </div>
    <div
      className="isp-confirm-row-value"
      style={{
        flex: wrapValue ? "1 1 260px" : 1,
        minWidth: 0,
        fontWeight: 500,
        wordBreak: wrapValue ? "normal" : "break-word",
      }}
    >
      {value}
    </div>
  </div>
);

const SectionHeading = ({ children }) => (
  <h5
    style={{
      color: "#385EA4",
      borderBottom: "2px solid #385EA4",
      paddingBottom: "4px",
      marginTop: "20px",
      marginBottom: "8px",
      fontSize: "15px",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    }}
  >
    {children}
  </h5>
);

const FeeStageHeading = ({ children }) => (
  <div
    className="isp-fee-stage-heading"
    style={{
      backgroundColor: "#385EA4",
      color: "#fff",
      fontWeight: 700,
      fontSize: "14px",
      textTransform: "uppercase",
      marginTop: "12px",
      marginBottom: "8px",
      padding: "8px 12px",
    }}
  >
    {children}
  </div>
);

const FeeSubheading = ({ children }) => (
  <div
    className="isp-fee-subheading"
    style={{
      backgroundColor: "#e9ecef",
      color: "#343a40",
      fontWeight: 700,
      fontSize: "13px",
      marginTop: "8px",
      padding: "6px 10px",
    }}
  >
    {children}
  </div>
);

const ISPConfirm = ({
  state,
  lists,
  isFormValid,
  onSubmit,
  isSubmitting,
  submitError,
  viewOnly = false,
}) => {
  const {
    advisorName,
    additionalAdvisors = [],
    custodian,
    strategy,
    strategyDisplayName,
    modelSelection,
    bondType,
    bondTypePromptShown,
    bondTypePreferenceProvided,
    pathStrategy,
    pathBondType,
    pathIarFeeType,
    pathIarFeeFlat,
    pathIarFeeTiered,
    hasPath,
    registrations,
    requestType,
    existingRequestAction,
    accountNumber,
    accountNumbers,
    feeUpdateAccounts,
    feeUpdateStrategyFeeBlocks,
    clientProfileImaSubmitted,
    householding,
    householdRefName,
    householdRefLast4,
    householdRef,
    householdReason,
    householdingMode,
    initialInvestmentAmount,
    accountNetWorthPct,
    accountIncomeNeed,
    accountGoal,
    accountGoalOther,
    sourceOfFunds,
    fusionRegistrationType,
    pcsRegistrationType,
    fusionRebalanceType,
    jointClients,
    combinedScore,
    riskCategory,
    selectedPortfolio,
    portfolioOverride,
    iarFeeTier,
    iarFeeType,
    iarFeeFlat,
    iarFeeTiered,
    iarFeeScheduleType,
    customFees,
    managementFees,
    fusionAllocations,
    fusionSubFees,
    selectedManagementFeeTierKey,
    hasInitialFeeStage: storedHasInitialFeeStage,
  } = state;

  const isFusion = strategy?.toLowerCase().includes("fusion") || false;
  const isManagedMoneyMarket = String(strategy || "")
    .toLowerCase()
    .includes("managed money market solution");
  const isFusionMultiRegistration = isFusion && fusionRegistrationType === "Multi";
  const isModelChange = existingRequestAction === "Model Change";
  const isFeeUpdate =
    requestType === "Existing" && existingRequestAction === "Fee Update";
  const rawFeeUpdateAccounts = Array.isArray(feeUpdateAccounts)
    ? feeUpdateAccounts
    : [];
  const isPcsRegistrationTypeEligible =
    supportsPcsRegistrationType(requestType, existingRequestAction) &&
    (isFeeUpdate
      ? rawFeeUpdateAccounts.length > 0 &&
        rawFeeUpdateAccounts.every((account) =>
          isPcsRegistrationStrategy(
            account?.strategy,
            lists?.proposalTypes || [],
          ),
        )
      : isPcsRegistrationStrategy(strategy, lists?.proposalTypes || []));
  const isPcsMultiRegistration =
    isPcsRegistrationTypeEligible && pcsRegistrationType === "Multi";
  const standardStrategies = Array.from(
    new Set(
      rawFeeUpdateAccounts
        .map((account) => String(account?.strategy || "").trim())
        .filter(Boolean),
    ),
  );
  const isStandardRegistrationTypeEligible =
    supportsStandardRegistrationType(requestType, existingRequestAction) &&
    (isFeeUpdate
      ? standardStrategies.length === 1 &&
        rawFeeUpdateAccounts.every((account) =>
          isStandardRegistrationStrategy(
            account?.strategy,
            lists?.proposalTypes || [],
          ),
        )
      : isStandardRegistrationStrategy(
          strategy,
          lists?.proposalTypes || [],
        ));
  const isStandardMultiRegistration =
    isStandardRegistrationTypeEligible &&
    state.standardRegistrationType === "Multi";
  const isRiskToleranceUpdate =
    requestType === "Existing" &&
    existingRequestAction === "Risk Tolerance Update";
  const showBondTypePreference =
    bondTypePromptShown === true &&
    isRiskToleranceUpdate &&
    bondTypePreferenceProvided === true &&
    ["Taxable", "Municipal"].includes(bondType);
  const showBondType =
    bondTypePromptShown === true &&
    ["Taxable", "Municipal"].includes(bondType) &&
    (!isRiskToleranceUpdate || showBondTypePreference);
  const showClientProfileIma =
    requestType === "New" ||
    (requestType === "Existing" &&
      isStrategyChangeWorkflowAction(existingRequestAction));
  const showHouseholding =
    requestType === "New" && householdingMode !== "disallowed";
  const advisorNamesDisplay = [
    advisorName,
    ...(Array.isArray(additionalAdvisors) ? additionalAdvisors : []),
  ]
    .map((name) => String(name || "").trim())
    .filter(Boolean)
    .join(", ");
  const effectiveFusionRebalanceType = isFusionMultiRegistration
    ? "Manual"
    : fusionRebalanceType || "Automatic";
  const strategyDisplay = strategyDisplayName ||
    getProgramDisplayName(strategy, lists?.proposalTypes);
  const hasInitialFeeStage =
    storedHasInitialFeeStage === true ||
    (isElementsStrategyName(strategy) && managementFees?.flatFee != null);

  const primaryClient = jointClients.find((c) => c.isPrimary);
  const registrationValue =
    isFusionMultiRegistration ||
    isPcsMultiRegistration ||
    isStandardMultiRegistration
    ? ""
    : registrations[0]?.value || "";
  const registrationDisplay =
    isFusionMultiRegistration ||
    isPcsMultiRegistration ||
    isStandardMultiRegistration
    ? ""
    : (registrations || [])
        .map((registration) => registration.value)
        .filter(Boolean)
        .join(", ");
  const registrationTypes = lists?.registrationTypes || [];
  const registrationDetailRows =
    isFusionMultiRegistration ||
    isPcsMultiRegistration ||
    isStandardMultiRegistration
    ? []
    : getRegistrationDetailReviewRows(
        state,
        registrationValue,
        registrationTypes,
      );
  const accountNumberDisplay = (
    accountNumbers && accountNumbers.length
      ? accountNumbers
      : accountNumber
        ? [accountNumber]
        : []
  )
    .map((number) => String(number || "").trim())
    .filter(Boolean)
    .map((number) => `****${number}`)
    .join(", ");
  const normalizedFeeUpdateAccounts = isFeeUpdate
    ? normalizeFeeUpdateAccounts({
        feeUpdateAccounts,
        accountNumbers,
        accountNumber,
        registrations,
        strategy,
      }, {
        registrationTypes,
      })
    : [];
  const requestTypeDescription = requestType
    ? requestType === "New"
      ? "New Business"
      : "Existing Account Update"
    : "\u2014";
  const riskScoreDescription =
    combinedScore !== "" || riskCategory
      ? `${combinedScore !== "" ? combinedScore : "\u2014"}/100${
          riskCategory ? ` \u2014 ${riskCategory}` : ""
        }`
      : "\u2014";
  const portfolioDescription = selectedPortfolio
    ? `${selectedPortfolio}${portfolioOverride ? " \u2014 Override" : ""}`
    : "\u2014";
  // T8 \u2014 household reference now carries name + last-4 separately. Fall
  // back to the legacy householdRef field for older drafts that pre-date
  // the split.
  const householdingValue =
    householding === "Yes"
      ? `Yes \u2014 ${householdRefName ? `${householdRefName}, ` : ""}Account ****${
          householdRefLast4 || householdRef || ""
        }`
      : householding === "No"
      ? `Not Applicable${householdReason ? ` \u2014 ${householdReason}` : ""}`
      : "\u2014";

  const goalValue = Array.isArray(accountGoal)
    ? accountGoal.length
      ? accountGoal.join(", ")
      : "\u2014"
    : accountGoal || "\u2014";
  const goalExplanation =
    (Array.isArray(accountGoal) && accountGoal.includes("Other")) ||
    accountGoal === "Funding for another goal"
      ? accountGoalOther || "\u2014"
      : "";

  const renderStandardRegistrationDetails = (row, index) => {
    const prefix = `Registration ${index + 1}`;
    const rowGoal = Array.isArray(row.accountGoal)
      ? row.accountGoal.join(", ")
      : row.accountGoal;
    const incomeDetails = row.accountIncomeNeed === "Yes"
      ? [
          row.accountIncomeAmount,
          row.accountIncomeFrequency,
          row.accountIncomeStartDate,
          row.accountIncomeEndDate,
        ]
          .filter(Boolean)
          .join(" | ")
      : "";
    return (
      <React.Fragment key={`${row.accountOwner}-${row.value || row.registration}-${index}`}>
        <Row
          label={prefix}
          value={[
            row.accountOwner,
            row.accountLast4 && `****${row.accountLast4}`,
            row.value || row.registration,
          ]
            .filter(Boolean)
            .join(" | ") || "\u2014"}
          wrapValue
        />
        <Row
          label={`${prefix} \u2014 Initial Amount of Investment`}
          value={row.initialInvestmentAmount || "\u2014"}
        />
        <Row
          label={`${prefix} \u2014 % of Net Worth`}
          value={
            row.accountNetWorthPct !== "" && row.accountNetWorthPct !== undefined
              ? `${row.accountNetWorthPct}%`
              : "\u2014"
          }
        />
        <Row
          label={`${prefix} \u2014 Need for Income`}
          value={row.accountIncomeNeed || "\u2014"}
        />
        {incomeDetails && (
          <Row
            label={`${prefix} \u2014 Income Details`}
            value={incomeDetails}
            wrapValue
          />
        )}
        <Row
          label={`${prefix} \u2014 Primary Goal`}
          value={rowGoal || "\u2014"}
          wrapValue
        />
        {row.accountGoalOther && (
          <Row
            label={`${prefix} \u2014 Goal Explanation`}
            value={row.accountGoalOther}
            wrapValue
          />
        )}
        {row.bondType && (
          <Row
            label={`${prefix} \u2014 Bond Type Preference`}
            value={row.bondType}
          />
        )}
      </React.Fragment>
    );
  };

  const formatPct = (v) => formatIarFeePercent(v);

  const StackedSchedule = ({ entries }) => (
    <div style={{ display: "grid", gap: "2px", maxWidth: "520px" }}>
      {entries.map(({ label, rate }, index) => (
        <div
          key={`${label}-${index}`}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <span>{label}</span>
          <span style={{ whiteSpace: "nowrap" }}>{formatPct(rate)}</span>
        </div>
      ))}
    </div>
  );

  const renderTierSchedule = (entries) =>
    entries.length ? <StackedSchedule entries={entries} /> : "\u2014";

  const forceFixedIarFee =
    hasPath || (managementFees?.flatFee !== undefined && managementFees?.flatFee !== null);
  const initialIarFeeType = iarFeeType || (forceFixedIarFee ? "Fixed Percentage" : "");
  const visibleIarFeeScheduleName = getVisibleIarFeeScheduleName(
    customFees,
    iarFeeScheduleType,
  );
  const iarFeeValue =
    initialIarFeeType === "Fixed Percentage"
      ? `Fixed Percentage \u2014 ${formatPct(iarFeeFlat)}`
      : initialIarFeeType === "Flat Fee"
      ? `Flat Fee \u2014 $${Number(iarFeeFlat || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : initialIarFeeType === "Tiered"
      ? visibleIarFeeScheduleName
        ? `Tiered (${visibleIarFeeScheduleName})`
        : "Tiered"
      : "\u2014";
  const pathIarFeeValue =
    pathIarFeeType === "Fixed Percentage"
      ? `Fixed Percentage \u2014 ${formatPct(pathIarFeeFlat)}`
      : pathIarFeeType === "Flat Fee"
      ? `Flat Fee \u2014 $${Number(pathIarFeeFlat || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : pathIarFeeType === "Tiered"
      ? visibleIarFeeScheduleName
        ? `Tiered (${visibleIarFeeScheduleName})`
        : "Tiered"
      : "\u2014";

  // T17 \u2014 surface the IM fee schedule on the Confirm screen so reviewers
  // see both fee components, not only the IAR fee.
  // T4 \u2014 for Fusion, build per-sleeve summary lines from fusionSubFees;
  // for non-Fusion, fall back to the single managementFees schedule.
  const fusionFeeSummaries =
    isFusion && Array.isArray(fusionAllocations)
      ? fusionAllocations
          .filter((a) => a && a.strategy)
          .map((a) => {
            const tier = selectedManagementFeeTierKey || "";
            const fees = resolveFusionSubFees(
              fusionSubFees,
              a.strategy,
              tier,
            );
            const feeValidation = validateManagementFeeTable(fees);
            const entries = feeValidation.tieredRates;
            const allocationLabel =
              isPrivateClientStrategyAlias(
                a.strategy,
                lists?.proposalTypes || [],
              ) || !a.displayName
                ? getFusionAllocationDisplayName(
                    { ...a, taxSensitivity: "" },
                    lists?.proposalTypes || [],
                  )
                : a.displayName;
            const allocationDetails = [
              a.accountOwner && `Owner: ${a.accountOwner}`,
              a.registration && `Registration: ${a.registration}`,
            ].filter(Boolean);
            return {
              strategy: allocationDetails.length
                ? `${allocationLabel} (${allocationDetails.join("; ")})`
                : allocationLabel,
              weight: a.percentage,
              entries,
              missingSchedule: !feeValidation.isComplete,
            };
          })
      : [];

  const imTiered =
    managementFees?.tieredRates && Array.isArray(managementFees.tieredRates)
      ? managementFees.tieredRates
      : [];
  const imTieredEntries = imTiered.map((t) => ({
    label: t.label,
    rate: t.rate,
  }));
  const normalizedFeeUpdateFeeBlocks = Array.isArray(
    feeUpdateStrategyFeeBlocks,
  )
    ? feeUpdateStrategyFeeBlocks.map((block) => ({
        strategy: block?.strategy || block?.rawStrategy || "Strategy",
        tier: block?.tier || "",
        entries: Array.isArray(block?.tieredRates)
          ? block.tieredRates.map((entry) => ({
              label: entry?.label || entry?.tier || "",
              rate: entry?.rate ?? entry?.fee ?? "",
            }))
          : [],
        flatFeeRate: block?.flatFeeRate ?? "",
        minimumNotes: Array.isArray(block?.minimumNotes)
          ? block.minimumNotes.filter(Boolean)
          : [],
      }))
    : [];

  const iarTieredEntries =
    initialIarFeeType === "Tiered" ? toTieredEntries(iarFeeTiered) : [];

  const pathIarTieredEntries =
    hasPath && pathIarFeeType === "Tiered" ? toTieredEntries(pathIarFeeTiered) : [];

  return (
    <div>
      {!viewOnly && <h1>Confirm:</h1>}

      <div style={{ margin: "12px 0 20px" }}>
        <SectionHeading>Client Information</SectionHeading>
        <Row
          label="Investment Advisor Representative (IAR)"
          value={advisorNamesDisplay || "\u2014"}
        />
        <Row label="Request Type" value={requestTypeDescription} />
        {existingRequestAction && (
          <Row label="Account Update Type" value={existingRequestAction} />
        )}
        {!isFeeUpdate && accountNumberDisplay && (
          <Row
            label="Account Number(s) (Last 4 digits)"
            value={accountNumberDisplay}
          />
        )}
        {showClientProfileIma && (
          <Row
            label="Client Profile + IMA submitted"
            value={clientProfileImaSubmitted || "\u2014"}
          />
        )}
        {showHouseholding && <Row label="Householding" value={householdingValue} />}
        {primaryClient && (
          <Row
            label="Primary Account Holder"
            value={
              primaryClient.ssnLast4
                ? `${primaryClient.clientName}  (SSN ****${primaryClient.ssnLast4})`
                : primaryClient.clientName
            }
          />
        )}
        {jointClients.length > 1 && (
          <Row
            label="Account Holders"
            value={jointClients.map((c) => c.clientName).join(", ")}
          />
        )}

        <SectionHeading>
          {isFeeUpdate ? "Fee Update Accounts" : "Account Information"}
        </SectionHeading>
        {isFeeUpdate && isPcsRegistrationTypeEligible && (
          <>
            <Row
              label="PCS Registration Type"
              value={pcsRegistrationType || "Single"}
            />
            <Row label="Strategy" value={strategyDisplay || "\u2014"} />
          </>
        )}
        {isFeeUpdate && isStandardRegistrationTypeEligible && (
          <>
            <Row
              label="Registration Type"
              value={state.standardRegistrationType || "Single"}
            />
            <Row label="Strategy" value={strategyDisplay || "\u2014"} />
          </>
        )}
        {isFeeUpdate &&
          normalizedFeeUpdateAccounts.map((account, index) => {
            const accountDetailRows = getRegistrationDetailReviewRows(
              account,
              account.registration,
              registrationTypes,
            );
            return isStandardMultiRegistration
              ? renderStandardRegistrationDetails(account, index)
              : (
              <React.Fragment
                key={`${account.accountLast4}-${account.registration}-${index}`}
              >
                <Row
                  label={`Account ${index + 1}`}
                  value={[
                    isPcsMultiRegistration && account.accountOwner,
                    account.accountLast4 && `****${account.accountLast4}`,
                    account.registration,
                    !isPcsRegistrationTypeEligible &&
                      getProgramDisplayName(
                        account.strategy,
                        lists?.proposalTypes,
                      ),
                  ]
                    .filter(Boolean)
                    .join(" | ")}
                  wrapValue
                />
                {accountDetailRows.map((detail) => (
                  <Row
                    key={`${detail.label}-${detail.value}`}
                    label={`Account ${index + 1} — ${detail.label}`}
                    value={detail.value}
                    wrapValue
                  />
                ))}
              </React.Fragment>
              );
          })}
        {!isFeeUpdate && (
          <>
        {custodian && <Row label="Custodian" value={custodian} />}
        {isFusion && (
          <Row
            label="Registration Type"
            value={fusionRegistrationType || "Single"}
          />
        )}
        {isFusion && (
          <Row
            label="Rebalance Type"
            value={effectiveFusionRebalanceType}
          />
        )}
        {isPcsRegistrationTypeEligible && (
          <Row
            label="PCS Registration Type"
            value={pcsRegistrationType || "Single"}
          />
        )}
        {isStandardRegistrationTypeEligible && (
          <Row
            label="Registration Type"
            value={state.standardRegistrationType || "Single"}
          />
        )}
        {isPcsMultiRegistration &&
          (registrations || []).map((registration, index) => (
            <Row
              key={`${registration.accountOwner}-${registration.value}-${index}`}
              label={`Registration ${index + 1}`}
              value={[registration.accountOwner, registration.value]
                .filter(Boolean)
                .join(" | ") || "\u2014"}
              wrapValue
            />
          ))}
        {isStandardMultiRegistration &&
          (registrations || []).map((registration, index) =>
            renderStandardRegistrationDetails(registration, index),
          )}
        {!isFusionMultiRegistration &&
          !isPcsMultiRegistration &&
          !isStandardMultiRegistration && (
          <Row
            label="Registration"
            value={registrationDisplay || registrationValue || "\u2014"}
          />
        )}
        {registrationDetailRows.map((detail) => (
          <Row
            key={`${detail.label}-${detail.value}`}
            label={detail.label}
            value={detail.value}
            wrapValue
          />
        ))}
        <Row label="Strategy" value={strategyDisplay || "\u2014"} />
        {isManagedMoneyMarket && (
          <Row
            label="Money Market Fund"
            value={modelSelection || selectedPortfolio || "\u2014"}
          />
        )}
        {!isStandardMultiRegistration && !isManagedMoneyMarket && showBondType && (
          <Row
            label={isRiskToleranceUpdate ? "Bond Type Preference" : "Bond Type"}
            value={bondType}
          />
        )}
        {pathBondType && (
          <Row
            label="Path Bond Type"
            value={
              pathStrategy
                ? `${pathBondType} (${pathStrategy})`
                : pathBondType
            }
          />
        )}
        {!isStandardMultiRegistration && (
          <>
            <Row
              label="Initial Investment Amount"
              value={initialInvestmentAmount || "\u2014"}
            />
            {accountNetWorthPct && (
              <Row label="% of Net Worth" value={`${accountNetWorthPct}%`} />
            )}
            <Row label="Income Need" value={accountIncomeNeed || "No"} />
            <Row label="Primary Goal" value={goalValue} />
            {goalExplanation && (
              <Row label="Goal Explanation" value={goalExplanation} />
            )}
          </>
        )}
        <Row
          label="Source of Funds"
          value={sourceOfFunds.join(", ") || "\u2014"}
        />
          </>
        )}

        {!isFeeUpdate && (
          <>
            <SectionHeading>Risk Tolerance</SectionHeading>
            {!isModelChange && (
              <Row label="Risk Score Total" value={riskScoreDescription} />
            )}
            <Row
              label={
                isManagedMoneyMarket
                  ? "Selected Money Market Fund"
                  : isModelChange
                    ? "Allocation"
                    : "Portfolio"
              }
              value={portfolioDescription}
            />
          </>
        )}

        {!isModelChange && !isRiskToleranceUpdate && <>
        <SectionHeading>Fees</SectionHeading>
        {hasInitialFeeStage ? (
          <>
            <div data-testid="review-initial-elements-fee-stage">
              <FeeStageHeading>
                {ELEMENTS_INITIAL_FEE_STAGE_HEADING}
              </FeeStageHeading>
              <FeeSubheading>Investment Management Fee</FeeSubheading>
              {managementFees?.flatFee != null && (
                <Row
                  label="Fixed Percentage - All Assets"
                  value={formatPct(managementFees.flatFee)}
                />
              )}
              {managementFees?.minimumFee && (
                <Row
                  label="Minimum Account Fee"
                  value={`$${managementFees.minimumFee}`}
                />
              )}
              <FeeSubheading>IAR Fee</FeeSubheading>
              <Row label="Fee Type" value={iarFeeValue} />
              {iarTieredEntries.length > 0 && (
                <Row
                  label="Tier Schedule"
                  value={renderTierSchedule(iarTieredEntries)}
                  wrapValue
                />
              )}
            </div>

            {hasPath && (
              <div data-testid="review-path-elements-fee-stage">
                <FeeStageHeading>
                  {getElementsPathFeeStageHeading(pathStrategy)}
                </FeeStageHeading>
                <FeeSubheading>Investment Management Fee</FeeSubheading>
                {!isManagedMoneyMarket && iarFeeTier && iarFeeTier !== "Standard" && (
                  <Row label="Pricing Tier" value={iarFeeTier} />
                )}
                <Row
                  label="Fee Schedule"
                  value={renderTierSchedule(imTieredEntries)}
                  wrapValue
                />
                {managementFees?.pathMinimumFee && (
                  <Row
                    label="Minimum Account Fee"
                    value={`$${managementFees.pathMinimumFee}`}
                  />
                )}

                <FeeSubheading>IAR Fee</FeeSubheading>
                <Row label="Fee Type" value={pathIarFeeValue} />
                {pathIarTieredEntries.length > 0 && (
                  <Row
                    label="Tier Schedule"
                    value={renderTierSchedule(pathIarTieredEntries)}
                    wrapValue
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <Row label="IAR Fee" value={iarFeeValue} />
            {iarTieredEntries.length > 0 && (
              <Row
                label="IAR Tier Schedule"
                value={renderTierSchedule(iarTieredEntries)}
                wrapValue
              />
            )}
            {!isManagedMoneyMarket && iarFeeTier && iarFeeTier !== "Standard" && (
              <Row label="Investment Management Fee Tier" value={iarFeeTier} />
            )}
          </>
        )}
        {/* T17 + T4 - for Fusion, render one row per sub-strategy with its own
            tier schedule; for non-Fusion, render the single IM schedule. */}
        {isFeeUpdate && normalizedFeeUpdateFeeBlocks.length > 0 ? (
          normalizedFeeUpdateFeeBlocks.map((block, index) => (
            <React.Fragment key={`${block.strategy}-${index}`}>
              <FeeStageHeading>
                Investment Management Fee — {block.strategy}
              </FeeStageHeading>
              {block.tier && block.tier !== "Standard" && (
                <Row label="Investment Management Fee Tier" value={block.tier} />
              )}
              {block.flatFeeRate !== "" && (
                <Row
                  label="Fixed Percentage - All Assets"
                  value={`All assets: ${formatPct(block.flatFeeRate)}`}
                />
              )}
              {block.entries.length > 0 && (
                <Row
                  label="Investment Management Schedule"
                  value={renderTierSchedule(block.entries)}
                  wrapValue
                />
              )}
              {block.minimumNotes.length > 0 && (
                <Row
                  label="Minimum Account Fee"
                  value={block.minimumNotes.join(" ")}
                  wrapValue
                />
              )}
            </React.Fragment>
          ))
        ) : !hasInitialFeeStage && isFusion ? (
          fusionFeeSummaries.length > 0 ? (
            fusionFeeSummaries.map((s, idx) => (
              <Row
                key={idx}
                label={`IM Schedule - ${s.strategy} (${Number(s.weight).toFixed(0)}%)`}
                value={
                  s.missingSchedule ? (
                    <span
                      className="text-danger"
                      role="alert"
                      data-testid="fusion-review-missing-fee-schedule"
                    >
                      Management fee schedule unavailable for the selected
                      tier.
                    </span>
                  ) : (
                    renderTierSchedule(s.entries)
                  )
                }
                wrapValue
              />
            ))
          ) : (
            <Row label="Investment Management Schedule" value="\u2014" />
          )
        ) : !hasInitialFeeStage ? (
          <>
          <Row
            label="Investment Management Schedule"
            value={renderTierSchedule(imTieredEntries)}
            wrapValue
          />
            {managementFees?.minimumFee && (
              <Row
                label="IM Minimum Account Fee"
                value={`$${managementFees.minimumFee}`}
              />
            )}
            {managementFees?.flatFee != null && (
              <Row
                label="Fixed Percentage - All Assets"
                value={`All assets: ${formatPct(managementFees.flatFee)}`}
              />
            )}
          </>
        ) : null}
        </>}
      </div>

      {/* Error Alert */}
      {submitError && !viewOnly && (
        <div className="alert alert-danger" role="alert">
          {submitError}
        </div>
      )}

      {/* Submit */}
      {!viewOnly && (
        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-lg btn-success"
            style={{
              minWidth: "200px",
            }}
            disabled={!isFormValid || isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? (
              <span>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                />
                Submitting...
              </span>
            ) : (
              "Submit ISP"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ISPConfirm;
